/**
 * "Load Replay" modal: textarea for pasting a replay string. Dual-purpose:
 * when given a `prefill` string it acts as a copy-out fallback for when
 * the clipboard API is unavailable.
 */

import * as LevelStore from '../../../editor/LevelStore.js';
import type { LevelData } from '../../../gameplay/LevelLoader.js';
import { parseReplay, type Replay } from '../../../gameplay/Replay.js';
import { parseShareLink, type SharePayload } from '../../../gameplay/ShareLink.js';
import { createModalShell, styleButton } from './modalUtils.js';

export interface LoadReplayController { close(): void }

export type LoadReplayTarget =
  | { kind: 'builtin'; index: number }
  | { kind: 'user'; data: LevelData };

export interface LoadReplayOptions {
  /** Optional initial textarea content (also flips the title to copy mode). */
  prefill?: string;
  /** Built-in level catalog used to resolve replay.levelId. */
  allLevels: ReadonlyArray<LevelData>;
  /** Called once the user clicks Load with a valid replay whose level was
   *  resolved against allLevels or LevelStore. The host should set its
   *  pendingReplay then load the level. The modal closes itself first. */
  onLoad: (replay: Replay, target: LoadReplayTarget) => void;
  /** Called when the user pastes a full share URL (with `?r=...`). The
   *  host receives the parsed payload (replay + optional inline level)
   *  and is expected to start playback in the current instance. */
  onLoadShare?: (payload: SharePayload) => void;
  onClose?: () => void;
}

export function openLoadReplayModal(opts: LoadReplayOptions): LoadReplayController {
  const prefill = opts.prefill ?? '';
  const shell = createModalShell({
    width: 'min(560px, 92vw)',
    maxHeight: '80vh',
    ...(opts.onClose ? { onClose: opts.onClose } : {}),
  });

  const title = document.createElement('div');
  title.textContent = prefill ? 'Replay text (copy below)' : 'Paste a replay';
  Object.assign(title.style, { fontSize: '16px', fontWeight: '700' } as Partial<CSSStyleDeclaration>);
  shell.panel.appendChild(title);

  const ta = document.createElement('textarea');
  ta.value = prefill;
  ta.placeholder = 'Paste a share URL (https://\u2026/?r=\u2026)\nor a SHKR1 replay text:\n\nSHKR1 level=01 seed=...\nD 0 100,200\nU 30\n...';
  Object.assign(ta.style, {
    width: '100%', minHeight: '220px', resize: 'vertical',
    background: '#0e0820', color: '#eee', border: '1px solid #444',
    borderRadius: '6px', padding: '8px', font: '12px ui-monospace, Menlo, monospace',
  } as Partial<CSSStyleDeclaration>);
  shell.panel.appendChild(ta);

  const status = document.createElement('div');
  Object.assign(status.style, { minHeight: '18px', color: '#f88' } as Partial<CSSStyleDeclaration>);
  shell.panel.appendChild(status);

  const buttons = document.createElement('div');
  Object.assign(buttons.style, { display: 'flex', gap: '8px', justifyContent: 'flex-end' } as Partial<CSSStyleDeclaration>);
  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'Close';
  styleButton(closeBtn);
  closeBtn.addEventListener('click', shell.close);
  const loadBtn = document.createElement('button');
  loadBtn.textContent = 'Load & Play';
  styleButton(loadBtn, true);
  loadBtn.addEventListener('click', () => {
    void (async () => {
      const raw = ta.value.trim();
      // Detect a share URL (or bare query string) and route through the
      // share-link decoder. We accept full URLs, scheme-less host paths,
      // and a lone `?r=...&z=1` fragment so users can paste whatever
      // their messaging app handed them.
      const looksLikeShare = /[?&]r=/.test(raw) || raw.startsWith('?r=') || raw.startsWith('r=');
      if (looksLikeShare && opts.onLoadShare) {
        try {
          const search = raw.includes('?') ? raw.slice(raw.indexOf('?')) : `?${raw.replace(/^[?&]/, '')}`;
          const payload = await parseShareLink(search);
          if (payload) {
            shell.close();
            opts.onLoadShare(payload);
            return;
          }
          status.textContent = 'Share link is missing replay data.';
          return;
        } catch (err) {
          status.textContent = (err instanceof Error ? err.message : String(err))
            || 'Could not decode share link.';
          return;
        }
      }
      const r = parseReplay(raw);
      if (!r) { status.textContent = 'Invalid replay text or share URL.'; return; }
      const builtinIdx = opts.allLevels.findIndex((l) => l.id === r.levelId);
      if (builtinIdx >= 0) {
        shell.close();
        opts.onLoad(r, { kind: 'builtin', index: builtinIdx });
        return;
      }
      const user = LevelStore.listUser().find((l) => l.data.id === r.levelId);
      if (user) {
        shell.close();
        opts.onLoad(r, { kind: 'user', data: user.data });
        return;
      }
      status.textContent = `Level "${r.levelId}" not found.`;
    })();
  });
  buttons.appendChild(closeBtn);
  buttons.appendChild(loadBtn);
  shell.panel.appendChild(buttons);

  document.body.appendChild(shell.overlay);
  return { close: shell.close };
}
